'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Instagram double-tap heart.
 *
 * Timings match the real thing closely: the heart punches in past full size,
 * settles, holds, then lifts and fades. Instagram's overshoot is the whole
 * character of the gesture — a plain fade reads as a loading state instead of a
 * reaction.
 */
export function HeartBurst({ burstKey }: { burstKey: number | null }) {
    return (
        <AnimatePresence>
            {burstKey !== null && (
                <motion.div
                    key={burstKey}
                    initial={{ opacity: 0, scale: 0.3 }}
                    animate={{
                        opacity: [0, 1, 1, 0],
                        scale: [0.3, 1.25, 1, 1.1],
                        y: [0, 0, 0, -28],
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.05, times: [0, 0.22, 0.6, 1], ease: 'easeOut' }}
                    className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="#fff"
                        aria-hidden="true"
                        className="w-28 h-28 drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]"
                    >
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/**
 * Double-tap / double-click detection that coexists with the modal's swipe
 * navigation.
 *
 * Touch handlers on the player frame bubble to the backdrop, which uses vertical
 * swipes to move between reels. So a touch only counts as a tap when the finger
 * stayed within `MOVE_TOLERANCE` — otherwise a fast flick would register as a
 * like on the way past.
 */
const DOUBLE_TAP_MS = 300;
const MOVE_TOLERANCE = 12;
/** Window in which clicks are ignored because touch already handled the gesture. */
const CLICK_SUPPRESSION_MS = 700;

export function useDoubleTap(onDoubleTap: () => void, onSingleTap?: () => void) {
    const lastTap = useRef(0);
    const start = useRef({ x: 0, y: 0 });
    const moved = useRef(false);
    const lastTouchEndAt = useRef(0);
    const suppressUntil = useRef(0);
    /**
     * A double-tap also fires two ordinary clicks. On the reel that meant
     * pause-then-play — the video visibly stuttered every time someone liked it.
     * The single-tap action is therefore deferred by the double-tap window and
     * cancelled the moment a second tap arrives, so only one of the two
     * interpretations ever runs.
     */
    const pendingSingle = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelPendingSingle = useCallback(() => {
        if (pendingSingle.current) {
            clearTimeout(pendingSingle.current);
            pendingSingle.current = null;
        }
    }, []);

    const queueSingleTap = useCallback(() => {
        if (!onSingleTap) return;
        cancelPendingSingle();
        pendingSingle.current = setTimeout(() => {
            pendingSingle.current = null;
            onSingleTap();
        }, DOUBLE_TAP_MS);
    }, [onSingleTap, cancelPendingSingle]);

    useEffect(() => cancelPendingSingle, [cancelPendingSingle]);

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        start.current = { x: touch.clientX, y: touch.clientY };
        moved.current = false;
    }, []);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (
            Math.abs(touch.clientX - start.current.x) > MOVE_TOLERANCE ||
            Math.abs(touch.clientY - start.current.y) > MOVE_TOLERANCE
        ) {
            moved.current = true;
        }
    }, []);

    const onTouchEnd = useCallback(() => {
        if (moved.current) return;

        const now = Date.now();
        lastTouchEndAt.current = now;

        if (now - lastTap.current < DOUBLE_TAP_MS) {
            cancelPendingSingle();
            // Hold off the click the browser is about to synthesise from this
            // same tap, which would otherwise re-queue the pause we just cancelled.
            suppressUntil.current = now + CLICK_SUPPRESSION_MS;
            onDoubleTap();
            lastTap.current = 0; // Prevent a third tap chaining another like.
        } else {
            lastTap.current = now;
            queueSingleTap();
        }
    }, [onDoubleTap, cancelPendingSingle, queueSingleTap]);

    const onDoubleClick = useCallback(() => {
        cancelPendingSingle();
        suppressUntil.current = Date.now() + CLICK_SUPPRESSION_MS;
        onDoubleTap();
    }, [onDoubleTap, cancelPendingSingle]);

    /**
     * Mouse path only. Touch input already resolved itself in onTouchEnd, and the
     * click browsers synthesise afterwards would double-book the single-tap
     * action — so anything arriving right after a touch is ignored outright.
     */
    const onClick = useCallback(
        (e: React.MouseEvent) => {
            const now = Date.now();
            if (now < suppressUntil.current) return;
            if (now - lastTouchEndAt.current < CLICK_SUPPRESSION_MS) return;

            if (e.detail > 1) {
                cancelPendingSingle();
                return;
            }
            queueSingleTap();
        },
        [cancelPendingSingle, queueSingleTap]
    );

    return { onTouchStart, onTouchMove, onTouchEnd, onDoubleClick, onClick, cancelPendingSingle };
}

const HINT_STORAGE_KEY = 'nebras:gallery-gesture-hint';
const HINT_VISIBLE_MS = 3200;

/**
 * One-time gesture coaching.
 *
 * Swipe-to-navigate and double-tap-to-like were both undiscoverable — nothing on
 * screen suggested the reel responded to either. This shows once per device and
 * then stays out of the way; returning visitors already know.
 */
export function useGestureHint(isOpen: boolean) {
    const [showHint, setShowHint] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        let alreadySeen = false;
        try {
            alreadySeen = window.localStorage.getItem(HINT_STORAGE_KEY) === 'seen';
        } catch {
            // Private mode / blocked storage: show the hint rather than suppress it.
        }
        if (alreadySeen) return;

        setShowHint(true);
        const timer = setTimeout(() => {
            setShowHint(false);
            try {
                window.localStorage.setItem(HINT_STORAGE_KEY, 'seen');
            } catch {
                // Not persisting just means it shows again next visit.
            }
        }, HINT_VISIBLE_MS);

        return () => clearTimeout(timer);
    }, [isOpen]);

    return showHint;
}

export function GestureHint({
    show,
    swipeLabel,
    doubleTapLabel,
}: {
    show: boolean;
    swipeLabel: string;
    doubleTapLabel: string;
}) {
    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35 }}
                    className="pointer-events-none absolute inset-0 z-40 flex flex-col items-center justify-center gap-3"
                >
                    <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-white/15 bg-black/70 px-6 py-5 text-center backdrop-blur-md">
                        <motion.span
                            animate={{ y: [0, -7, 0] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                            className="text-2xl leading-none"
                            aria-hidden="true"
                        >
                            ↑
                        </motion.span>
                        <span className="font-heading text-sm font-semibold text-white">{swipeLabel}</span>
                        <span className="h-px w-10 bg-white/20" />
                        <span className="text-2xl leading-none" aria-hidden="true">
                            👆
                        </span>
                        <span className="font-heading text-sm font-semibold text-white">{doubleTapLabel}</span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** Drives one burst per like gesture. */
export function useHeartBurst() {
    const [burstKey, setBurstKey] = useState<number | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const burst = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        setBurstKey(Date.now());
        timer.current = setTimeout(() => setBurstKey(null), 1100);
    }, []);

    return { burstKey, burst };
}
